(async() => {
    const { Core } = await importModule('Core');
    const {
        arrEq,
        $JSON,
        cache,
        fetchHTML
    } = await importModule('Utils');


    const CN_UNAVAILABLE = '!unavailable';
    const CN_SOLD_OUT = '!sold-out';
    const CN_FITMENT_MISMATCH = '!fitment-mismatch';
    const ATTR_UNAVAILABLE = 'unavailable';
    const ATTR_OPTION_INDEX = 'data-option-index';
    const ATTR_VARIANT_ID = 'variant-id';
    const ATTR_SELLING_PLAN_ID = 'selling-plan-id';
    const ATTR_PRODUCT_URL = 'product-url';
    const ATTR_SMART_SELECT = 'smart-select';
    const VEHICLE_STORAGE_KEY = 'unleashed:selected-vehicle';
    const DISABLE_FILTER_KEY = 'unleashed:fitment-filter-disabled';
    // Customer's opt-in to picking their own offset. Absent = default: Wheel Specs values
    // that share a diameter/width collapse to one pick with the offset hidden, and Unleashed
    // Wheels reviews the order and ships the right offset manually — this key is independent
    // of which vehicle is selected, so it isn't reset when the vehicle changes or is cleared.
    const MANUAL_OFFSET_KEY = 'unleashed:manual-offset-enabled';
    // Matches a single offset ("ET34") as well as the ET ranges blank/custom-drilled specs
    // carry ("ET15-45") — the exact offset within that range is machined to the vehicle, not
    // picked by the customer, so it's stripped from display either way.
    const RE_OFFSET_TOKEN = /\s*ET-?\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*/i;
    // Some brands (MV Forged, AL13, etc.) spell the same offset range as "to 23" instead of
    // an ET-prefixed range — same meaning, stripped the same way.
    const RE_TO_RANGE_TOKEN = /\s*\bto\s+\d+(?:\.\d+)?\s*/i;
    // Center bore ("CB73.1", or a range like "CB57.1-74.1") — most customers don't know what
    // this number means, so it's hidden the same way as offset unless they opt into full specs.
    const RE_CENTER_BORE_TOKEN = /\s*CB-?\d+(?:\.\d+)?(?:-\d+(?:\.\d+)?)?\s*/i;
    const ATTR_ORIGINAL_TEXT = 'data-original-text';

    const normalizeBoltPattern = (s) => String(s).toUpperCase().replace(/\s+/g, '');
    // Blank/custom-drilled wheels (Stance, Variant, Vertini, etc.) are machined to order
    // after purchase, so they fit any vehicle and should never fade as a mismatch.
    const isBlankBoltPattern = (s) => normalizeBoltPattern(s).includes('BLANK');

    customElements.define('product-variant-selector', class extends Core {

        elements = {
            variantsJSON: '[data-variants-json]',
            optionsJSON: '[data-options-json]',
            inventoryJSON: '[data-inventory-json]',
            displayValues: '*[data-display-value]',
            selectors: '*[data-option-selector]',
            variantIdInput: 'input[name="id"]',
            autoOffsetToggle: '[data-auto-offset-toggle]',
            autoOffsetWrap: '[data-auto-offset-wrap]',
            autoOffsetProperty: '[data-auto-offset-property]',
            manualOffsetProperty: '[data-manual-offset-property]',
            vehicleProperty: '[data-vehicle-property]'
        }

        subscriptions = {
            'selling-plan:change': '_onSellingPlanChange',
            'cart:add': '_onCartAdd',
            'cart:change': '_onCartChange',
            'vehicle:change': '_onVehicleChange',
        }

        render() {
            this.options = $JSON(this.$optionsJSON);
            this.variants = $JSON(this.$variantsJSON);
            this.variantsInventory = $JSON(this.$inventoryJSON);

            this._handleCartQty();
            this._handleSellingPlanChange();
            this._getDefaultVariant();
            this._initCache();
            this._handleOptionChange();
            this._initVehicleFitment();
            this.$variantIdInput.disabled = false;
        }

        _initVehicleFitment() {
            this.compatibleBoltPatterns = this._readStoredBoltPatterns();
            this.compatibleWheelDiameterRange = this._readStoredWheelDiameterRange();
            this.manualOffsetEnabled = localStorage.getItem(MANUAL_OFFSET_KEY) === '1';
            this.subscribe('vehicle:change', { global: true });
            this._bindAutoOffsetToggle();
            this._syncAutoOffsetUI();
            this._syncManualOffsetProperty();
            this._syncVehicleProperty(this._readStoredVehicleLabel());
            this._applyFitmentState();
        }

        // Lets staff see on the order whether the customer switched on "Show full wheel specs"
        // and picked their own offset/center bore, rather than leaving it to the default
        // auto-fit — a hidden (underscore-prefixed) line item property, so it shows in the
        // Shopify admin order view but not in the customer-facing cart/checkout/emails.
        _syncManualOffsetProperty() {
            if (!this.$manualOffsetProperty) return;
            this.$manualOffsetProperty.disabled = !this.manualOffsetEnabled || this._hasFixedOffsets() || this._noVehicleFitment();
        }

        // Independent of bolt-pattern filtering/matching — staff want to know which vehicle
        // the customer selected even if it doesn't fit this product or the filter is off.
        _readStoredVehicleLabel() {
            try {
                const raw = localStorage.getItem(VEHICLE_STORAGE_KEY);
                if (!raw) return null;
                const vehicle = JSON.parse(raw);
                if (!vehicle?.year || !vehicle?.make || !vehicle?.model) return null;
                return `${vehicle.year} ${vehicle.make} ${vehicle.model}`;
            } catch (e) {
                return null;
            }
        }

        _syncVehicleProperty(label) {
            if (!this.$vehicleProperty) return;
            if (!label) {
                this.$vehicleProperty.disabled = true;
                return;
            }
            this.$vehicleProperty.value = label;
            this.$vehicleProperty.disabled = false;
        }

        // Checkbox represents "I want to choose my own offset" — checked shows every real
        // offset; unchecked (default) collapses same-width offsets to one pick per width.
        _bindAutoOffsetToggle() {
            this.$autoOffsetToggle?.addEventListener('change', (e) => {
                // This checkbox lives inside <product-variant-selector> for placement, but it
                // is not an option input — stop it here so it never reaches the delegated
                // option-change handler below (which would otherwise treat it as a Bolt
                // Pattern change, since a plain checkbox has no data-option-index and its
                // default value is the string "on").
                e.stopPropagation();
                this.manualOffsetEnabled = e.target.checked;
                if (this.manualOffsetEnabled) {
                    localStorage.setItem(MANUAL_OFFSET_KEY, '1');
                } else {
                    localStorage.removeItem(MANUAL_OFFSET_KEY);
                }
                this._syncManualOffsetProperty();
                this._setOptionsAvailability();
                this._reselectGroupRepresentative();
            });
        }

        // The toggle only makes sense once a vehicle is active, and only for a real preset
        // bolt pattern — a Blank (Custom) selection has no fixed offset to pick between since
        // it's machined to order, so hide the toggle whenever that's the active pattern.
        _syncAutoOffsetUI() {
            if (this.$autoOffsetToggle) this.$autoOffsetToggle.checked = this.manualOffsetEnabled;
            if (this.$autoOffsetWrap) {
                this.$autoOffsetWrap.hidden = this._hasFixedOffsets() || !this.compatibleBoltPatterns || isBlankBoltPattern(this.currentOptions[0]);
            }
        }

        _wheelSpecsOptionIndex() {
            return this.options.findIndex(o => (o.name || '').trim().toLowerCase() === 'wheel specs');
        }

        _wheelDiameterOptionIndex() {
            return this.options.findIndex(o => (o.name || '').trim().toLowerCase() === 'wheel diameter');
        }

        _parseDiameter(value) {
            const m = String(value).match(/(\d+(?:\.\d+)?)/);
            return m ? parseFloat(m[1]) : null;
        }

        _fitsSelectedVehicleSize(value) {
            if (!this.compatibleWheelDiameterRange) return true;
            const diameter = this._parseDiameter(value);
            if (diameter == null) return true;
            const [min, max] = this.compatibleWheelDiameterRange;
            return diameter >= min && diameter <= max;
        }

        // Brands whose offsets are fixed per SKU (4Play) set the `fixed-offsets` attribute from
        // product-variant-selector.liquid — there is no offset for staff to "choose before
        // shipping", so the real spec (offset + center bore) always stays visible.
        _hasFixedOffsets() {
            return this.hasAttribute('fixed-offsets');
        }

        // Accessories set the `no-vehicle-fitment` attribute from product-variant-selector.liquid.
        // They aren't wheels, so the saved vehicle's bolt pattern/size never fades or auto-picks
        // their options and the "Show full wheel specs" toggle never appears — all of that keys
        // off compatibleBoltPatterns / compatibleWheelDiameterRange staying null.
        _noVehicleFitment() {
            return this.hasAttribute('no-vehicle-fitment');
        }

        _shouldGroupOffsets() {
            return !this._hasFixedOffsets() && !!this.compatibleBoltPatterns && !this.manualOffsetEnabled && !isBlankBoltPattern(this.currentOptions[0]);
        }

        // Real presets: strip the offset from the label only while grouping same-width
        // variants together (so the customer isn't shown a single offset as if it were fixed).
        // Blank/custom specs: always strip it — the ET range is machined per order, never a
        // real pick, regardless of grouping/toggle state.
        _shouldStripOffset(value, index) {
            if (index !== this._wheelSpecsOptionIndex()) return false;
            return this._shouldGroupOffsets() || isBlankBoltPattern(value);
        }

        _stripOffset(value) {
            return value.replace(RE_OFFSET_TOKEN, ' ').replace(RE_TO_RANGE_TOKEN, ' ').replace(RE_CENTER_BORE_TOKEN, ' ').replace(/\s{2,}/g, ' ').trim();
        }

        // Blank/custom specs collapse unconditionally (there's no real offset left to
        // distinguish duplicates by once it's stripped, and no toggle to opt out of it — see
        // _syncAutoOffsetUI). Real presets only collapse while _shouldGroupOffsets() holds.
        _shouldCollapseGroups() {
            return isBlankBoltPattern(this.currentOptions[0]) || this._shouldGroupOffsets();
        }

        // The customer still needs to pick a width (20X9 vs 20X10 vs 20X11) — only the
        // specific offset within that width is what Unleashed Wheels decides later. Restores
        // every label first, then collapses values sharing the same diameter/width/bolt
        // pattern (differing only by offset) down to a single visible swatch with the offset
        // stripped from its label.
        _applyOffsetGrouping() {
            const specIndex = this._wheelSpecsOptionIndex();
            if (specIndex === -1) return;

            const values = this.options[specIndex].values;
            values.forEach(v => {
                const input = this._getInputEl(v, specIndex);
                const titleEl = input?.closest('label')?.querySelector('[class*="item-title"]');
                if (!titleEl) return;
                if (!titleEl.hasAttribute(ATTR_ORIGINAL_TEXT)) {
                    titleEl.setAttribute(ATTR_ORIGINAL_TEXT, titleEl.textContent);
                }
                titleEl.textContent = isBlankBoltPattern(v)
                    ? this._stripOffset(v)
                    : titleEl.getAttribute(ATTR_ORIGINAL_TEXT);
            });

            if (!this._shouldCollapseGroups()) return;

            const groups = {};
            values.forEach(v => {
                if (!this._existsFor(v, specIndex)) return;
                const key = this._stripOffset(v);
                (groups[key] ??= []).push(v);
            });

            Object.entries(groups).forEach(([strippedLabel, candidates]) => {
                const representative = candidates[0];
                candidates.forEach(v => {
                    const input = this._getInputEl(v, specIndex);
                    const label = input?.closest('label');
                    if (!label) return;
                    if (v === representative) {
                        const titleEl = label.querySelector('[class*="item-title"]');
                        if (titleEl) titleEl.textContent = strippedLabel;
                        label.title = isBlankBoltPattern(v)
                            ? 'We\'ll machine the exact offset for this width to fit your vehicle'
                            : 'We\'ll select the exact offset for this width before shipping';
                    } else {
                        label.style.display = 'none';
                    }
                });
            });
        }

        _groupRepresentative(specIndex, key) {
            return this.options[specIndex].values.find(v =>
                this._existsFor(v, specIndex) && this._stripOffset(v) === key
            );
        }

        // Keeps the actually-selected variant in sync with the visible representative for its
        // width group — otherwise the "selected" swatch could be one that's now hidden. Only
        // call once Bolt Pattern + Diameter have already stabilized (see _applyFitmentState).
        _reselectGroupRepresentative() {
            if (!this._shouldCollapseGroups()) return false;
            const specIndex = this._wheelSpecsOptionIndex();
            if (specIndex === -1) return false;

            const current = this.currentOptions[specIndex];
            const representative = this._groupRepresentative(specIndex, this._stripOffset(current));
            if (!representative || representative === current) return false;

            return this._dispatchOptionChange(representative, specIndex);
        }

        _readStoredBoltPatterns() {
            if (this._noVehicleFitment()) return null;
            try {
                if (localStorage.getItem(DISABLE_FILTER_KEY) === '1') return null;
                const raw = localStorage.getItem(VEHICLE_STORAGE_KEY);
                if (!raw) return null;
                const vehicle = JSON.parse(raw);
                return vehicle?.boltPatterns?.length ? vehicle.boltPatterns.map(normalizeBoltPattern) : null;
            } catch (e) {
                return null;
            }
        }

        // Wheel diameter range (e.g. [19, 20]) — optional, only set for vehicles with a
        // confirmed real size range. A "Blank (Custom)" bolt pattern otherwise matches ANY
        // vehicle needing that pattern regardless of whether the wheel is even offered in a
        // size that clears the car (e.g. a 22"-only wheel showing as a fit for a car topping
        // out at 20").
        _readStoredWheelDiameterRange() {
            if (this._noVehicleFitment()) return null;
            try {
                if (localStorage.getItem(DISABLE_FILTER_KEY) === '1') return null;
                const raw = localStorage.getItem(VEHICLE_STORAGE_KEY);
                if (!raw) return null;
                const vehicle = JSON.parse(raw);
                if (vehicle?.wheelDiameterMin == null || vehicle?.wheelDiameterMax == null) return null;
                return [vehicle.wheelDiameterMin, vehicle.wheelDiameterMax];
            } catch (e) {
                return null;
            }
        }

        _onVehicleChange(data) {
            const fitmentApplies = !this._noVehicleFitment();
            this.compatibleBoltPatterns = (fitmentApplies && data?.boltPatterns?.length) ? data.boltPatterns.map(normalizeBoltPattern) : null;
            this.compatibleWheelDiameterRange = (fitmentApplies && data?.wheelDiameterMin != null && data?.wheelDiameterMax != null)
                ? [data.wheelDiameterMin, data.wheelDiameterMax]
                : null;
            const vehicleLabel = (data?.year && data?.make && data?.model) ? `${data.year} ${data.make} ${data.model}` : null;
            this._syncVehicleProperty(vehicleLabel);
            this._syncAutoOffsetUI();
            this._applyFitmentState();
        }

        // keepBlank: true when the customer changed Diameter / Wheel Specs (not the vehicle) — an
        // explicitly chosen "Blank (Custom)" bolt pattern fits any vehicle, so it must not be
        // swapped back to the vehicle's own bolt pattern just because they picked another size.
        _applyFitmentState({ keepBlank = false } = {}) {
            this._autoSelectFittingBoltPattern({ keepBlank });
            this._setOptionsAvailability();
            this._reselectGroupRepresentative();
        }

        _dispatchOptionChange(value, index) {
            const input = this._getInputEl(value, index);
            if (!input) return false;
            input.checked = true;
            // A plain Event, not CustomEvent — _optionChangeHandler deliberately ignores
            // CustomEvent instances (used elsewhere to sync inputs without recursing), so
            // only a plain Event actually drives a real option-change here.
            input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
            return true;
        }

        _autoSelectFittingBoltPattern({ keepBlank = false } = {}) {
            if (!this.compatibleBoltPatterns) return false;
            const current = this.currentOptions[0];
            if (keepBlank && isBlankBoltPattern(current)) return false;
            if (this.compatibleBoltPatterns.includes(normalizeBoltPattern(current))) return false;

            const match = this.options[0].values.find(v => this.compatibleBoltPatterns.includes(normalizeBoltPattern(v)));
            return match ? this._dispatchOptionChange(match, 0) : false;
        }

        _fitsSelectedVehicle(value) {
            if (!this.compatibleBoltPatterns) return true;
            if (isBlankBoltPattern(value)) return true;
            return this.compatibleBoltPatterns.includes(normalizeBoltPattern(value));
        }

        _handleCartQty() {
            this.variants.map(variant => {
                if (this.variantsInventory[variant.id]) {
                    variant.available_to_add = +this.variantsInventory[variant.id].available_to_add;
                    variant.inventory_quantity = +this.variantsInventory[variant.id].inventory_quantity;
                }
            })
            this.subscribe('cart:add', {global: true});
            this.subscribe('cart:change', {global: true});
        }

        _onCartAdd({ items }) {
            items.map(addedItem => {
                const matchedVariant = this.variants.find(variant => variant.id === +addedItem.id);
                if (matchedVariant) {
                    matchedVariant.available_to_add -= +addedItem.quantity;
                    if (matchedVariant.id === this.variant.id && matchedVariant.available_to_add <= 0) {
                        this._updateCurrent();
                    }
                }
            })
        }

        async _onCartChange(item) {
            const matchedVariant = this.variants.find(variant => variant.id === +item.id);
            if (matchedVariant && matchedVariant.inventory_quantity) {
                const oldAvailableToAdd = matchedVariant.available_to_add
                const newAvailableToAdd = matchedVariant.inventory_quantity - +item.quantity;
                matchedVariant.available_to_add = newAvailableToAdd;
                if (matchedVariant.id === this.variant.id) {
                    if (oldAvailableToAdd <= 0 && newAvailableToAdd > 0 || newAvailableToAdd <= 0) {
                        this._updateCurrent();
                    }
                    return
                }
                this._clearVariantCache(matchedVariant.id, item.selling_plan)
            }
        }

        async _updateCurrent() {
            setTimeout(async () => {
                await this._fetchVariantDOM();
                this._publishChanges();
            }, 500)
        }

        _clearVariantCache(variantId, sellingPlanId) {
            cache.set(`${variantId}${sellingPlanId || ''}`, undefined);
        }

        _handleSellingPlanChange() {
            if (this.hasAttribute(ATTR_SELLING_PLAN_ID)) {
                this.sellingPlanId = this.getAttribute(ATTR_SELLING_PLAN_ID);
                this.subscribe('selling-plan:change');
            }
        }

        _handleOptionChange() {
            this._setOptionsAvailability();
            this.addEventListener('change', this._optionChangeHandler.bind(this));
        }

        _getDefaultVariant() {
            this.variant = this.variants.find(({ id }) => id === +this.getAttribute(ATTR_VARIANT_ID));
            this.currentOptions = [...this.variant.options];
        }

        _initCache() {
            cache.set(`${this.variant.id}${this.sellingPlanId || ''}`, this.getParentSection()?.cloneNode(true));
        }

        _setOptionsAvailability() {
            this.options
                .reduce(this._getOptionsState.bind(this), [...new Set()])
                .map(this._toggleOptionAviability.bind(this))
            this._syncAutoOffsetUI();
            this._applyOffsetGrouping();
            this._syncOffsetProperty();
            this._refreshDisplayValues();
        }

        // Attaches a cart line item property carrying the same ET-stripped label shown on
        // the product page, so the cart display stays consistent instead of revealing the
        // real offset the moment the item is added — Shopify renders variant options in the
        // cart directly from the variant itself, which this can't relabel.
        _syncOffsetProperty() {
            if (!this.$autoOffsetProperty) return;
            const specIndex = this._wheelSpecsOptionIndex();
            const currentSpec = specIndex === -1 ? null : this.currentOptions[specIndex];

            if (currentSpec == null || !this._shouldStripOffset(currentSpec, specIndex)) {
                this.$autoOffsetProperty.disabled = true;
                return;
            }

            this.$autoOffsetProperty.value = this._stripOffset(currentSpec);
            this.$autoOffsetProperty.disabled = false;
        }

        _optionChangeHandler(e) {
            // Guard against any non-option control (e.g. the auto-offset toggle) bubbling a
            // native 'change' event into this delegated handler — it has no data-option-value,
            // and treating it as an option change corrupts currentOptions.
            if (!e.target.hasAttribute('data-option-value')) return;
            if(!(e instanceof CustomEvent)) {
                const optionIndex = +e.target.getAttribute(ATTR_OPTION_INDEX);
                this.currentOptions[optionIndex] = e.target.value;
                !this.smartSelect && this.classList.toggle(CN_UNAVAILABLE, false);
                // Only block unavailable selections for lower-level options (index > 0)
                if (!this.smartSelect && e.target.hasAttribute(ATTR_UNAVAILABLE) && optionIndex > 0) {
                    this.classList.toggle(CN_UNAVAILABLE, true);
                    this._updateDisplayValue(e.target);
                    this.publish('variant:change');
                    this._setOptionsAvailability();
                    return;
                }
                this._updateVariantWithOption(e.target);
                this._setOptionsAvailability();
                // The underlying variant-resolution fallback (_getAvailableVariant) can land
                // on a different Bolt Pattern than the one already selected when a Diameter or
                // Wheel Specs change has no exact-match variant — e.g. it can silently revert
                // to whatever pattern happens to be listed first. Re-assert the vehicle's
                // fitment here (but only for changes the customer didn't make directly to
                // Bolt Pattern itself, so an explicit mismatched choice is still respected).
                if (optionIndex !== 0) {
                    this._applyFitmentState({ keepBlank: true });
                } else {
                    // _applyFitmentState (and its group reselection) is skipped for a direct
                    // Bolt Pattern click, but the spec should still land on a visible
                    // representative within whatever pattern the customer just chose.
                    this._reselectGroupRepresentative();
                }
                this._publishChanges();
                if (this.isProductPage) this._updateHistory();
            }
            this._updateDisplayValue(e.target);
            this.$variantIdInput.value = this.variant.id;
        }

        _onSellingPlanChange({ sellingPlanId }) {
            this.sellingPlanId = sellingPlanId;
            this._publishChanges();
            if (this.isProductPage) this._updateHistory();
        }

        _updateDisplayValue(target) {
            if(!this.$displayValues.length) return;
            const match = this.$displayValues.find(item => item.getAttribute(ATTR_OPTION_INDEX) === target.getAttribute(ATTR_OPTION_INDEX));
            if (!match) return;
            const index = +target.getAttribute(ATTR_OPTION_INDEX);
            match.innerText = this._shouldStripOffset(target.value, index)
                ? this._stripOffset(target.value)
                : target.value;
        }

        // Covers the case where the heading needs to update without any option actually
        // changing — e.g. toggling "choose my own offset" on/off — which _updateDisplayValue
        // alone won't catch since it only runs off a real option-change event.
        _refreshDisplayValues() {
            if (!this.$displayValues.length) return;
            this.$displayValues.forEach(el => {
                const index = +el.getAttribute(ATTR_OPTION_INDEX);
                const value = this.currentOptions[index];
                if (value == null) return;
                el.innerText = this._shouldStripOffset(value, index) ? this._stripOffset(value) : value;
            });
        }

        async _publishChanges() {
            this.publish('variant:change', {
                doc: cache.get(`${this.variant.id}${this.sellingPlanId || ''}`) || await this._fetchVariantDOM(),
                variantId: this.variant.id,
                stock: this.variant.available,
                variantImagePosition: this.variant.featured_media?.position,
                sellingPlanId: this.sellingPlanId
            });
        }

        _updateHistory() {
            window.history.replaceState({}, null, `${window.location.pathname}?${this.historyURLParams.toString()}`);
        }

        async _fetchVariantDOM() {
            this.publish('variant:loading', true);
            const doc = await fetchHTML(this.requestURL);
            this.publish('variant:loading', false);
            cache.set(`${this.variant.id}${this.sellingPlanId || ''}`, doc);
            return doc;
        }

        _updateVariantWithOption(target) {
            const index = +target.getAttribute(ATTR_OPTION_INDEX);
            this.variant =
                this._getVariantFromValue(target.value, index) ||
                this._getAvailableVariant(target.value, index);
            this.currentOptions = [...this.variant.options];
            if(this.prevOptionIndex !== index) {
                this.prevOptionIndex = index;
            }
        }

        _getVariantFromValue(setValue, i) {
            let newOptions = [...this.currentOptions];
            newOptions[i] = setValue;
            return this.variants.find(({ options }) => arrEq(options, newOptions));
        }

        _getAvailableVariant(value, index) {
            const matchedVariants = this.variants.filter(variant => variant.options[index] === value);
            // Changing Diameter / Wheel Specs: stay on the bolt pattern already selected (e.g.
            // "Blank (Custom)") whenever that diameter offers it, instead of landing on whichever
            // variant happens to be listed first.
            const sameBolt = index !== 0
                ? matchedVariants.find(variant => variant.options[0] === this.currentOptions[0])
                : null;
            const match =
                sameBolt ||
                matchedVariants.find(this._isMatchPrevOption.bind(this)) ||
                matchedVariants[0];
            this._selectVariantOptions(match, index);
            return match;
        }

        _isMatchPrevOption(variant) {
            return variant.options[this.prevOptionIndex] === this.variant.options[this.prevOptionIndex];
        }

        _selectVariantOptions(src, ignore) {
            src.options.map((value, i) => {
                if(i !== ignore) {
                    const input = this._getInputEl(value, i);
                    input.checked = true;
                    input.dispatchEvent(new CustomEvent('change', {
                        bubbles: true,
                        cancelable: true
                    }));
                }
            })
        }

        // Helpers to compute availability progressively based on already chosen options
        _matchesUpToIndex(variant, i) {
            for (let j = 0; j < i; j++) {
                if (variant.options[j] !== this.currentOptions[j]) return false;
            }
            return true;
        }

        _existsFor(value, i) {
            return this.variants.some(v => v.options[i] === value && this._matchesUpToIndex(v, i));
        }

        _isAvailableFor(value, i) {
            return this.variants.some(v => v.options[i] === value && this._matchesUpToIndex(v, i) && v.available);
        }

        _getOptionsState(acc, option, i) {
            option.values.map(value => {
                const exists = this._existsFor(value, i);
                const available = exists && this._isAvailableFor(value, i);
                acc.push({ value, index: i, exists, available });
            })
            return acc;
        }

        _getInputEl(value, index) {
            return this.querySelector(`[value="${value.replace(/["\\]/g, '\\$&')}"][${ATTR_OPTION_INDEX}="${index}"]`)
        }

        _toggleOptionAviability({ value, index, exists, available }) {
            const $input = this._getInputEl(value, index);
            const label = $input.closest('label');

            if (index === 0) {
                // Bolt Pattern: always visible and never visually unavailable due to stock
                $input.classList.toggle(CN_UNAVAILABLE, false);
                $input.toggleAttribute(ATTR_UNAVAILABLE, false);
                if (label) label.style.display = '';

                // Vehicle fitment: fade patterns that don't match the customer's selected
                // vehicle, and disable the input so a faded/crossed-out option can't actually
                // be selected and added to cart — only "Disable Fitment Filter" (which empties
                // compatibleBoltPatterns entirely, so _fitsSelectedVehicle always returns true)
                // opens it back up.
                const fits = this._fitsSelectedVehicle(value);
                $input.classList.toggle(CN_FITMENT_MISMATCH, !fits);
                $input.disabled = !fits;
                if (label) {
                    label.classList.toggle(CN_FITMENT_MISMATCH, !fits);
                    if (isBlankBoltPattern(value)) {
                        label.title = 'Bolt pattern, offset, and center bore custom machined for your vehicle — 3-4 week lead time';
                    } else {
                        label.title = fits ? '' : 'This bolt pattern doesn\'t match your selected vehicle';
                    }
                }
            } else {
                // Lower levels: hide invalid values
                $input.classList.toggle(CN_UNAVAILABLE, !exists);
                $input.toggleAttribute(ATTR_UNAVAILABLE, !exists);
                if (label) label.style.display = !exists ? 'none' : '';

                // Wheel Diameter: fade (and disable selecting) sizes outside the selected
                // vehicle's confirmed range (e.g. a 22" option on a car that tops out at 20")
                // the same way Bolt Pattern fades/disables a non-matching pattern — a real
                // variant that exists and is in stock, just not one that would actually fit.
                if (index === this._wheelDiameterOptionIndex()) {
                    const fitsSize = this._fitsSelectedVehicleSize(value);
                    $input.classList.toggle(CN_FITMENT_MISMATCH, !fitsSize);
                    $input.disabled = !fitsSize;
                    if (label) {
                        label.classList.toggle(CN_FITMENT_MISMATCH, !fitsSize);
                        if (fitsSize) {
                            if (label.title === 'This size doesn\'t match your selected vehicle') label.title = '';
                        } else {
                            label.title = 'This size doesn\'t match your selected vehicle';
                        }
                    }
                }
            }

            // Sold-out styling when a value exists but is out of stock
            $input.classList.toggle(CN_SOLD_OUT, exists && !available);
        }

        get requestURL() {
            const params = new URLSearchParams({
                'selling_plan': this.sellingPlanId,
                'variant': this.variant.id,
                'section_id': this.sectionId
            });
            return `${this.productURL}?${params.toString()}`;
        }

        get productURL() {
            return this.getAttribute(ATTR_PRODUCT_URL);
        }

        get historyURLParams() {
            const params = new URLSearchParams({
                'variant': this.variant.id
            });
            this.sellingPlanId ? params.set('selling_plan', this.sellingPlanId) : params.delete('selling_plan');
            return new URLSearchParams(params);
        }

        get isProductPage() {
            return this.hasAttribute('product-page')
        }

        get smartSelect() {
            return this.hasAttribute(ATTR_SMART_SELECT)
        }

    });
})()
